import React, { Component } from "react";
import "./index.css";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

/* API requests */
import { updateCandidate, uploadCV } from "../../../../api-requests/candidate";

/* Actions */
import { updateUserProfile } from "../../../../redux/actions/user";
import { toggleCVPopup } from "../../../../redux/actions/initial-setup";

/* Components */
import DropPinGame from "../DropPinGame";

/* Helpers */
import { bytesToSize } from "../../../../helpers/misc";

class SelectProfileType extends Component {

    constructor(props) {
        super(props);

        this.processingMessages = [
            "Uploading your CV...",
            "Reading your document...",
            "Extracting your details...",
            "Analysing work experience...",
            "Almost there...",
        ];

        this.state = {
            loading: false,
            activeButton: "",
            error: false,
            errorMessage: "",
            processingStep: 0,
            // CV parse result is held here until the user clicks "Continue" so the
            // game can keep playing after parsing finishes. Without this we'd
            // dispatch initialSetupCompleted=true immediately and the parent
            // redirects away.
            pendingParseResult: null,
            // Game is opt-in. User clicks "Play a game while you wait?" to start.
            gameStarted: false,
        };

    }

    componentWillUnmount() {
        if (this.messageTimer) clearInterval(this.messageTimer);
    }

    startProcessingMessages = () => {
        this.setState({ processingStep: 0 });
        this.messageTimer = setInterval(() => {
            this.setState(prev => ({
                processingStep: Math.min(prev.processingStep + 1, this.processingMessages.length - 1)
            }));
        }, 5000);
    }

    handleBuildFromScratch = (user) => {

        // Hand the user over to the wizard at step 1 (Photo) so they go
        // through Photo → BasicInfo → IdealPosition → Qualifications, same
        // as the CV-upload path.
        const body = [
            { propName: "profileType", value: "build" },
            { propName: "initialSetupStep", value: 1 },
        ];

        return this.setState({ loading: true, activeButton: "build", error: false, errorMessage: "" }, () => {
            return updateCandidate(user.profile._id, body)
                .then(data => {
                    if (data.status === 201) {
                        this.props.updateUserProfile(body);
                        return;
                    }
                    return this.setState({ loading: false, activeButton: "" });
                });
        });

    }

    handleCVUpload = (file, user) => {

        const dotIndex = file.name.indexOf(".");

        const name = file.name.slice(0, dotIndex),
            extension = file.name.slice(dotIndex),
            size = bytesToSize(file.size);

        if (file) {
            this.startProcessingMessages();
            return this.setState({ loading: true, activeButton: "upload", error: false, errorMessage: "", pendingParseResult: null, gameStarted: false }, () => {
                return uploadCV(user.profile._id, file, name, extension, size)
                    .then(data => {
                        if (data.status === 201) {
                            if (this.messageTimer) clearInterval(this.messageTimer);
                            return this.setState({ pendingParseResult: data.body });
                        }
                        if (this.messageTimer) clearInterval(this.messageTimer);
                        return this.setState({ loading: false, activeButton: "", error: true, errorMessage: data.status === 500 ? "Uploading CV failed. You can upload another CV or build your profile from scratch." : data.error_message });
                    });
            });
        }

    }

    handleContinueToProfile = () => {
        const { pendingParseResult } = this.state;
        if (!pendingParseResult) return;
        // Server response already includes profileType, initialSetupStep: 3,
        // and the cv file metadata - just dispatch it as-is. The wizard takes
        // over at step 3 (IdealPosition) once initialSetupStep is set.
        this.props.updateUserProfile(pendingParseResult);
    }

    render() {

        const { loading, activeButton, processingStep, error, errorMessage, pendingParseResult, gameStarted } = this.state;
        const firstName = this.props.user.data ? this.props.user.data.firstName : "";
        const profileReady = !!pendingParseResult;

        return(
            <div className="page-container">
                <div className="select-profile-type-main-container">
                    <div className="select-profile-type-card">
                        <div className="select-profile-type-step-label">Step 1 of 2</div>
                        <h1 className="select-profile-type-title">
                            {loading && activeButton === "upload"
                                ? (profileReady ? "Your profile is ready" : "Reading your CV…")
                                : `Welcome aboard, ${firstName}`}
                        </h1>
                        <p className="select-profile-type-subtitle">
                            {loading && activeButton === "upload"
                                ? (profileReady
                                    ? "Continue when you're ready, or keep playing."
                                    : "This usually takes 30-60 seconds.")
                                : "Let's set up your profile. Upload your existing CV to save time, or build from scratch."}
                        </p>

                        {error && errorMessage &&
                            <div className="select-profile-type-error">
                                <p>{errorMessage}</p>
                            </div>
                        }

                        {!loading || activeButton !== "upload" ? (
                            <div className="select-profile-type-options">
                                <label className={`select-profile-type-option select-profile-type-option-primary${loading ? " is-disabled" : ""}`}>
                                    <input className="select-profile-type-file-input" hidden type="file" name="file" onChange={(e) => this.handleCVUpload(e.target.files[0], this.props.user.data)} />
                                    <div className="select-profile-type-option-icon">
                                        <svg width={28} height={28} viewBox="0 0 24 24" fill="none">
                                            <path d="M21 15V19A2 2 0 0119 21H5A2 2 0 013 19V15" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M17 8L12 3L7 8" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M12 3V15" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                    <div className="select-profile-type-option-body">
                                        <p className="select-profile-type-option-heading">Upload your CV</p>
                                        <p className="select-profile-type-option-detail">We'll read it and prefill your profile. About 30 seconds.</p>
                                    </div>
                                    <span className="select-profile-type-option-arrow">→</span>
                                </label>

                                <button
                                    type="button"
                                    className={`select-profile-type-option select-profile-type-option-secondary${loading ? " is-disabled" : ""}`}
                                    onClick={() => this.handleBuildFromScratch(this.props.user.data)}
                                >
                                    <div className="select-profile-type-option-icon">
                                        <svg width={28} height={28} viewBox="0 0 24 24" fill="none">
                                            <path d="M11 4H4A2 2 0 002 6V20A2 2 0 004 22H18A2 2 0 0020 20V13" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M18.5 2.5A2.121 2.121 0 0121.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                    <div className="select-profile-type-option-body">
                                        <p className="select-profile-type-option-heading">Build from scratch</p>
                                        <p className="select-profile-type-option-detail">Tell us about yourself section by section.</p>
                                    </div>
                                    <span className="select-profile-type-option-arrow">→</span>
                                </button>
                            </div>
                        ) : (
                            <div className="select-profile-type-progress">
                                <div className={`select-profile-type-progress-bar${profileReady ? " select-profile-type-progress-bar--complete" : ""}`}>
                                    <div className="select-profile-type-progress-fill" style={{width: profileReady ? "100%" : `${Math.min(15 + (processingStep * 20), 92)}%`}} />
                                </div>
                                <p className={`select-profile-type-progress-text${profileReady ? " select-profile-type-progress-text--ready" : ""}`}>
                                    {profileReady ? (
                                        <>
                                            <svg className="select-profile-type-progress-tick" width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                <path d="M5 12L10 17L20 7" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            Profile ready
                                        </>
                                    ) : this.processingMessages[processingStep]}
                                </p>
                                {profileReady && (
                                    <button
                                        type="button"
                                        className="select-profile-type-continue"
                                        onClick={this.handleContinueToProfile}
                                    >
                                        Continue to next step
                                        <span className="select-profile-type-continue-arrow">→</span>
                                    </button>
                                )}
                                {!gameStarted && !profileReady && (
                                    <button
                                        type="button"
                                        className="select-profile-type-play-game"
                                        onClick={() => this.setState({ gameStarted: true })}
                                    >
                                        <svg className="select-profile-type-play-game-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                            <path d="M9 4L3 6V20L9 18M9 4L15 6M9 4V18M15 6L21 4V18L15 20M15 6V20M9 18L15 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        Play a game while you wait?
                                    </button>
                                )}
                                {gameStarted && (
                                    <DropPinGame
                                        profileReady={profileReady}
                                        onContinue={this.handleContinueToProfile}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );

    }

}

const mapStateToProps = (state) => {
    return {
        user: state.user,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        ...bindActionCreators({ updateUserProfile, toggleCVPopup }, dispatch)
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(SelectProfileType);
